/**
 * Spec 10.2.1 §2.6 — transaction composer. Receipt / payment / journal with
 * multi-matter split lines and a live allocated / out-of-balance total; the
 * "Request authorisation to post" step drafts then requests authorisation (never
 * a direct post) — a signatory approves it from Needs You. Every draft is working
 * paper until authorised.
 */
import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { useLedgerCockpit, useComposeAndRequest, type ComposeInput } from "../../hooks/use-ledger-workbenches";
import { money } from "../../components/ledger/format";

type TxnType = "receipt" | "payment" | "journal";
interface SplitLine { account_id: string; amount: string; }
interface AcctOption { id: string; account_no: string; title_name: string; }

function toMinor(s: string): number { const n = Math.round(parseFloat(s.replace(/[^0-9.-]/g, "")) * 100); return Number.isFinite(n) ? n : 0; }

function AccountSelect({ value, onChange, placeholder, accounts }: { value: string; onChange: (v: string) => void; placeholder: string; accounts: AcctOption[] }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">{placeholder}</option>
      {accounts.map((a) => <option key={a.id} value={a.id}>{a.account_no} · {a.title_name}</option>)}
    </select>
  );
}

export function ComposerPage() {
  const { ledgerId } = useParams<{ ledgerId: string }>();
  const navigate = useNavigate();
  const cockpit = useLedgerCockpit(ledgerId);
  const compose = useComposeAndRequest();

  const [type, setType] = useState<TxnType>("receipt");
  const [effectiveDate, setEffectiveDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [reason, setReason] = useState("");
  const [method, setMethod] = useState("eft");
  const [party, setParty] = useState(""); // drawer (receipt) / payee (payment)
  const [instrumentKind, setInstrumentKind] = useState("eft");
  const [lines, setLines] = useState<SplitLine[]>([{ account_id: "", amount: "" }]);
  const [fromAcct, setFromAcct] = useState("");
  const [toAcct, setToAcct] = useState("");
  const [journalAmount, setJournalAmount] = useState("");

  const clientAccounts = useMemo(() => (cockpit.data?.accounts ?? []).filter((a) => a.kind !== "bank"), [cockpit.data]);
  const allocated = lines.reduce((s, l) => s + toMinor(l.amount), 0);

  const valid = (() => {
    if (!reason.trim()) return false;
    if (type === "journal") return !!fromAcct && !!toAcct && fromAcct !== toAcct && toMinor(journalAmount) > 0;
    return lines.some((l) => l.account_id && toMinor(l.amount) > 0) && lines.every((l) => !l.account_id || toMinor(l.amount) > 0);
  })();

  function submit() {
    const input: ComposeInput = { type, ledgerId: ledgerId!, effective_date: effectiveDate, reason: reason.trim() };
    if (type === "receipt") { input.method = method; input.counterparty_name = party || undefined; input.lines = lines.filter((l) => l.account_id).map((l) => ({ account_id: l.account_id, amount_minor: toMinor(l.amount) })); }
    else if (type === "payment") { input.payee = party || undefined; input.instrument_kind = instrumentKind; input.authorised_by = "practitioner"; input.lines = lines.filter((l) => l.account_id).map((l) => ({ account_id: l.account_id, amount_minor: toMinor(l.amount) })); }
    else { input.from_account_id = fromAcct; input.to_account_id = toAcct; input.amount_minor = toMinor(journalAmount); input.authorised_by = "practitioner"; }
    compose.mutate(input, { onSuccess: () => navigate("/app") });
  }

  const back = <div className="row" style={{ marginBottom: 10 }}><Link to={`/app/trust/${ledgerId}`} className="muted tiny" style={{ textDecoration: "none" }}>← Cockpit</Link></div>;

  return (
    <div className="screen">{back}
      <div className="surface">
        <div className="surface__bar"><div className="surface__title"><strong>New {type}</strong><span className="sub">{cockpit.data?.ledger.name ?? "Trust ledger"} · draft, not yet posted</span></div><span className="spacer" /><span className="pill pill--draft"><span className="dot" />draft</span></div>
        <div className="surface__body composer">
          <div>
            <div className="type-switch" role="tablist">
              {(["receipt", "payment", "journal"] as TxnType[]).map((t) => (
                <button key={t} role="tab" aria-selected={type === t} style={{ textTransform: "capitalize" }} onClick={() => setType(t)}>{t}</button>
              ))}
            </div>
            <div style={{ height: 16 }} />

            <div className="field-row">
              <div className="field"><label>Effective date</label><input type="date" value={effectiveDate} onChange={(e) => setEffectiveDate(e.target.value)} /></div>
              {type === "receipt" ? <div className="field"><label>Method</label><select value={method} onChange={(e) => setMethod(e.target.value)}><option value="eft">EFT</option><option value="cheque">Cheque</option><option value="cash">Cash</option><option value="direct_deposit">Direct deposit</option></select></div> : null}
              {type === "payment" ? <div className="field"><label>Instrument</label><select value={instrumentKind} onChange={(e) => setInstrumentKind(e.target.value)}><option value="eft">EFT</option><option value="cheque">Cheque</option><option value="bpay">BPay</option></select></div> : null}
            </div>

            {type !== "journal" ? <div className="field"><label>{type === "receipt" ? "Received from (drawer)" : "Paid to (payee)"}</label><input type="text" value={party} onChange={(e) => setParty(e.target.value)} /></div> : null}
            <div className="field"><label>Reason / purpose</label><input type="text" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Compulsory (statutory)" /></div>

            {type === "journal" ? (
              <>
                <div className="field"><label>From account (debited)</label><AccountSelect value={fromAcct} onChange={setFromAcct} placeholder="Choose source…" accounts={clientAccounts} /></div>
                <div className="field"><label>To account (credited)</label><AccountSelect value={toAcct} onChange={setToAcct} placeholder="Choose destination…" accounts={clientAccounts} /></div>
                <div className="field"><label>Amount</label><input type="text" className="tabular" value={journalAmount} onChange={(e) => setJournalAmount(e.target.value)} placeholder="0.00" /></div>
              </>
            ) : (
              <div className="field">
                <label>Allocation across matters (split)</label>
                <div className="split-lines">
                  {lines.map((l, i) => (
                    <div key={i} className="split-line">
                      <AccountSelect value={l.account_id} onChange={(v) => setLines((ls) => ls.map((x, j) => j === i ? { ...x, account_id: v } : x))} placeholder="Choose account…" accounts={clientAccounts} />
                      <input type="text" className="tabular" style={{ textAlign: "right" }} value={l.amount} placeholder="0.00" onChange={(e) => setLines((ls) => ls.map((x, j) => j === i ? { ...x, amount: e.target.value } : x))} />
                      <button aria-label="Remove line" onClick={() => setLines((ls) => ls.length > 1 ? ls.filter((_, j) => j !== i) : ls)}>−</button>
                    </div>
                  ))}
                  <div className="split-foot">
                    <button onClick={() => setLines((ls) => [...ls, { account_id: "", amount: "" }])}>+ Add matter line</button>
                    <span className="spacer" /><span className="muted tiny">allocated</span><span className="bal bal--ok money">{money(allocated)}</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          <aside className="stack">
            <div className="ledger-identity"><span className="statutory">{cockpit.data?.ledger.name ?? "Trust ledger"}</span><span className="bank">{cockpit.data?.ledger.profile ?? ""}</span></div>
            <div className="divider" />
            {compose.isError ? <div className="stale-banner"><svg className="icon" viewBox="0 0 24 24"><path d="M12 9v4m0 4h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" /></svg><div>{(compose.error as Error).message}</div></div> : null}
            <button data-variant="primary" disabled={!valid || compose.isPending} onClick={submit}>{compose.isPending ? "Requesting…" : "Request authorisation to post"}</button>
            <span className="muted tiny">Posting is gated: a signatory approves it from Needs You. Every draft is working paper until authorised.</span>
          </aside>
        </div>
      </div>
    </div>
  );
}
